"use client";

import { ArrowDown, ArrowUpRight, BriefcaseBusiness, Check, Mail } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, type FormEvent, type ReactNode } from "react";

import {
  marketingCopy,
  type MarketingLocale,
} from "@/features/marketing/content/marketing-copy";
import { submitDemoRequest } from "@/features/marketing/services/demo-request-service";

import styles from "./marketing-home.module.css";

type MarketingHomeProps = {
  locale: MarketingLocale;
};

type MarketingChromeProps = MarketingHomeProps & {
  linkToHomeSections?: boolean;
  localePath?: string;
};

export function MarketingHome({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale];

  return (
    <div className={styles.page}>
      <MarketingHeader locale={locale} />

      <main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.badge}><span aria-hidden="true" />{copy.badge}</p>
              <h1>{copy.title}</h1>
              <p className={styles.description}>{copy.description}</p>
              <div className={styles.ctas}>
                <a href="#demo" className={styles.primaryCta}>
                  {copy.scheduleDemo}<BriefcaseBusiness aria-hidden="true" />
                </a>
                <a href="#features" className={styles.secondaryCta}>
                  {copy.seeFeatures}<ArrowDown aria-hidden="true" />
                </a>
              </div>
              <ul className={styles.benefits}>
                {copy.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
              </ul>
            </div>

            <BuilderMockup locale={locale} />
          </div>
        </section>
        <FeatureSection locale={locale} />
        <BenefitsSection locale={locale} />
        <DashboardSection locale={locale} />
        <WhiteLabelSection locale={locale} />
        <CustomProjectSection locale={locale} />
        <JobSeekerSection locale={locale} />
        <AboutSection locale={locale} />
        <DemoSection locale={locale} />
      </main>
      <MarketingFooter locale={locale} />
    </div>
  );
}

function sectionHref(hash: string, locale: MarketingLocale, linkToHomeSections: boolean) {
  return linkToHomeSections ? `/?lang=${locale}${hash}` : hash;
}

function localizedPolicyHref(path: string, locale: MarketingLocale) {
  return locale === "fr" ? `${path}?lang=fr` : path;
}

export function MarketingHeader({
  locale,
  linkToHomeSections = false,
  localePath = "/",
}: MarketingChromeProps) {
  const copy = marketingCopy[locale];

  return (
    <header className={styles.header}>
      <nav className={styles.nav} aria-label="Primary navigation">
        <Link href={`/?lang=${locale}`} className={styles.brand} aria-label="CVBuilder.ca home">
          <Image
            src="/logo/Logo CV Builder.jpg"
            alt=""
            width={48}
            height={48}
            className={styles.logo}
            priority
          />
          <span><strong>CV</strong>Builder<span>.ca</span></span>
        </Link>

        <div className={styles.navLinks}>
          <a href={sectionHref("#features", locale, linkToHomeSections)}>{copy.nav.features}</a>
          <a href={sectionHref("#dashboard", locale, linkToHomeSections)}>{copy.nav.dashboard}</a>
          <a href={sectionHref("#white-label", locale, linkToHomeSections)}>{copy.nav.whiteLabel}</a>
          <a href={sectionHref("#about", locale, linkToHomeSections)}>{copy.nav.about}</a>
        </div>

        <div className={styles.navActions}>
          <div className={styles.localeSwitch} aria-label="Language">
            <Link href={`${localePath}?lang=en`} aria-current={locale === "en" ? "page" : undefined}>EN</Link>
            <span>/</span>
            <Link href={`${localePath}?lang=fr`} aria-current={locale === "fr" ? "page" : undefined}>FR</Link>
          </div>
          <a href={sectionHref("#demo", locale, linkToHomeSections)} className={styles.demoButton}>
            {copy.nav.requestDemo}
          </a>
        </div>
      </nav>
    </header>
  );
}

export function MarketingFooter({
  locale,
  linkToHomeSections = false,
}: MarketingChromeProps) {
  const copy = marketingCopy[locale].footer;
  const footerHrefs = [
    [
      sectionHref("#demo", locale, linkToHomeSections),
      sectionHref("#features", locale, linkToHomeSections),
      sectionHref("#features", locale, linkToHomeSections),
      sectionHref("#about", locale, linkToHomeSections),
    ],
    [
      localizedPolicyHref("/privacy", locale),
      localizedPolicyHref("/terms", locale),
    ],
  ] as const;
  const newTabLabel = locale === "fr" ? "s’ouvre dans un nouvel onglet" : "opens in a new tab";

  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Link href={`/?lang=${locale}`}><strong>CV</strong>Builder.ca</Link>
          <p>{copy.description}</p>
          <strong className={styles.hosting}>
            <svg
              className={styles.hostingFlag}
              viewBox="0 0 48 24"
              role="img"
              aria-label="Flag of Canada"
            >
              <rect width="48" height="24" fill="#fff" />
              <rect width="12" height="24" fill="#D52B1E" />
              <rect x="36" width="12" height="24" fill="#D52B1E" />
              <path
                fill="#D52B1E"
                d="M24 4.2l-1.4 2.6c-.2.3-.5.3-.8.1l-1-.5.7 3.6c.1.6-.3.6-.6.2l-1.4-1.6-.2.8c0 .2-.2.3-.4.3l-1.8-.4.5 1.7c.1.4.2.5-.1.7l-.6.3 3.3 2.7c.2.2.3.4.2.7l-.3 1 3.1-.4c.2 0 .4.1.4.3l-.2 3.3h.8l-.2-3.3c0-.2.2-.3.4-.3l3.1.4-.3-1c-.1-.3 0-.5.2-.7l3.3-2.7-.6-.3c-.3-.2-.2-.3-.1-.7l.5-1.7-1.8.4c-.2 0-.4-.1-.4-.3l-.2-.8-1.4 1.6c-.3.4-.7.4-.6-.2l.7-3.6-1 .5c-.3.2-.6.2-.8-.1z"
              />
            </svg>
            {copy.hosting}
          </strong>
        </div>

        {copy.groups.map((group, groupIndex) => (
          <nav className={styles.footerGroup} aria-label={group.title} key={group.title}>
            <h2>{group.title}</h2>
            <ul>
              {group.links.map((label, linkIndex) => (
                <li key={label}>
                  <Link
                    aria-label={groupIndex === 1 ? `${label} (${newTabLabel})` : undefined}
                    href={footerHrefs[groupIndex][linkIndex]}
                    rel={groupIndex === 1 ? "noopener noreferrer" : undefined}
                    target={groupIndex === 1 ? "_blank" : undefined}
                  >
                    <span aria-hidden={groupIndex === 1 ? "true" : undefined}>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        <div className={`${styles.footerGroup} ${styles.footerContact}`}>
          <h2>{copy.contact.title}</h2>
          <div className={styles.footerContactIcons}>
            <a
              href="mailto:team@cvbuilder.ca"
              aria-label={copy.contact.emailLabel}
              className={styles.footerContactIcon}
            >
              <Mail aria-hidden="true" />
            </a>
            <a
              href="https://www.facebook.com/12scinc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.contact.facebookLabel}
              className={styles.footerContactIcon}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M14 8.5V6.8c0-.8.2-1.3 1.4-1.3H17V2.3c-.3 0-1.3-.1-2.4-.1-2.4 0-4.1 1.5-4.1 4.2v2.1H8v3.6h2.5V22H14v-9.9h2.7l.4-3.6H14z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
      <p className={styles.copyright}>{copy.copyright}</p>
    </footer>
  );
}

function AboutSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].about;

  return (
    <section className={styles.aboutSection} id="about">
      <div className={styles.aboutInner}>
        <div className={styles.aboutGrid}>
          <div className={styles.aboutCopy}>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
            <div>{copy.description}</div>
          </div>

          <div className={styles.aboutPillars}>
            {copy.pillars.map((pillar) => (
              <article key={pillar.title}>
                <h3>{pillar.title}</h3>
                <p>{pillar.description}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function JobSeekerSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].jobSeekers;

  return (
    <section className={styles.jobSeekerSection} id="job-seekers">
      <div className={styles.jobSeekerInner}>
        <div className={styles.jobSeekerHeading}>
          <div>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
          </div>
        </div>

        <ol className={styles.jobSeekerSteps}>
          {copy.steps.map((step, index) => (
            <li key={step.title}>
              <span>{locale === "fr" ? "Étape" : "Step"} {index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>

        <div className={styles.dashboardCtaBand}>
          <p>{copy.cta.text}</p>
          <a href="#demo" className={styles.dashboardCtaLink}>
            {copy.cta.action}<ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function DashboardSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].dashboard;
  const { mockup } = copy;

  return (
    <section className={styles.dashboardSection} id="dashboard" aria-labelledby="dashboard-title">
      <div className={styles.dashboardInner}>
        <div className={styles.dashboardGrid}>
          <div className={styles.dashboardCopy}>
            <p className={styles.dashboardEyebrow}>{copy.eyebrow}</p>
            <h2 id="dashboard-title">{copy.title}</h2>
            <ul className={styles.dashboardChecklist}>
              {copy.checklist.map((item) => (
                <li key={item}><span><Check aria-hidden="true" /></span>{item}</li>
              ))}
            </ul>
          </div>

          <div className={styles.dashboardMockup} aria-label={copy.preview}>
            <div className={styles.dashboardMockupBar} aria-hidden="true">
              <span /><span /><span />
              <code>{mockup.url}</code>
            </div>
            <div className={styles.dashboardMockupBody}>
              <div className={styles.dashboardMockupHead}>
                <strong>{mockup.title}</strong>
                <span className={styles.dashboardPlan}>{mockup.planLabel}<em>{mockup.planStatus}</em></span>
              </div>

              <div className={styles.dashboardCard}>
                <div className={styles.dashboardCardHead}>
                  <span>{mockup.usageLabel}</span>
                  <em className={styles.dashboardUsageBadge}>{mockup.usageBadge}</em>
                </div>
                <p>{mockup.usageValue}<span>{mockup.usageTotal}</span></p>
                <div className={styles.dashboardProgress}><span style={{ width: "75%" }} /></div>
              </div>

              <div className={styles.dashboardCard}>
                <div className={styles.dashboardCardHead}>
                  <span>{mockup.statsLabel}</span>
                  <div className={styles.dashboardPeriods}>
                    {mockup.periods.map((period) => (
                      <span
                        key={period}
                        className={period === mockup.activePeriod ? styles.dashboardPeriodActive : undefined}
                      >
                        {period}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={styles.dashboardStatsGrid}>
                  {mockup.stats.map((stat) => (
                    <div key={stat.label}>
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.dashboardCtaBand}>
          <p>{copy.cta.text}</p>
          <a href="#demo" className={styles.dashboardCtaLink}>
            {copy.cta.action}<ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].orgBenefits;

  return (
    <section className={styles.benefitsSection} id="benefits">
      <div className={styles.benefitsInner}>
        <p className={styles.benefitsEyebrow}>{copy.eyebrow}</p>
        <h2>{copy.title}</h2>

        <div className={styles.benefitsGrid}>
          {copy.cards.map((card, index) => (
            <article className={styles.benefitsCard} key={card.title}>
              <span className={styles.benefitsCardNumber}>{String(index + 1).padStart(2, "0")}</span>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>

        <div className={styles.benefitsBuiltFor}>
          <span className={styles.benefitsBuiltForLabel}>{copy.builtForLabel}</span>
          <div className={styles.benefitsChips}>
            {copy.chips.map((chip) => (
              <span className={styles.benefitsChip} key={chip}>{chip}</span>
            ))}
          </div>
        </div>

        <div className={styles.dashboardCtaBand}>
          <p>{copy.cta.text}</p>
          <a href="#demo" className={styles.dashboardCtaLink}>
            {copy.cta.action}<ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function WhiteLabelSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].whiteLabel;

  return (
    <section className={styles.whiteLabelSection} id="white-label" aria-labelledby="white-label-title">
      <div className={styles.whiteLabelInner}>
        <div className={styles.whiteLabelGrid}>
          <div className={styles.whiteLabelCopy}>
            <p className={styles.whiteLabelEyebrow}>{copy.eyebrow}</p>
            <h2 id="white-label-title">{copy.title}</h2>
            <p>{copy.description}</p>

            <div className={styles.whiteLabelCta}>
              <strong>{copy.prompt}</strong>
              <a href="#demo">{copy.action}<span aria-hidden="true">↓</span></a>
            </div>
          </div>

          <div className={styles.signInPreview}>
            <Image
              src="/b2b-whitelabel-login.png"
              alt={copy.preview}
              width={1118}
              height={530}
              className={styles.signInPreviewImage}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function CustomProjectSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].customProject;

  return (
    <section className={styles.customProjectSection} aria-labelledby="custom-project-title">
      <div className={styles.customProjectInner}>
        <div className={styles.customProjectHeading}>
          <div>
            <p className={styles.whiteLabelEyebrow}>{copy.eyebrow}</p>
            <h2 id="custom-project-title">{copy.title}</h2>
          </div>
          <a href="#demo" className={styles.customProjectCta}>
            {copy.action}<ArrowUpRight aria-hidden="true" />
          </a>
        </div>

        <div className={styles.customProjectGrid}>
          {copy.cards.map((card) => (
            <div key={card.title} className={styles.customProjectCard}>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].demoSection;
  const { fields } = copy;
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");

    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      await submitDemoRequest({
        full_name: String(data.get("full-name") ?? ""),
        organization: String(data.get("organization") ?? ""),
        email: String(data.get("email") ?? ""),
        phone: String(data.get("phone") ?? ""),
        message: String(data.get("message") ?? ""),
        locale,
      });
      form.reset();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section className={styles.demoSection} id="demo">
      <div className={styles.demoInner}>
        <div className={styles.demoCopy}>
          <p className={styles.demoEyebrow}>{copy.eyebrow}</p>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
          <p className={styles.demoContact}>{copy.contactLine}</p>
          <ul>
            {copy.benefits.map((benefit) => (
              <li key={benefit}><span><Check aria-hidden="true" /></span>{benefit}</li>
            ))}
          </ul>
        </div>

        <form className={styles.demoForm} aria-label={copy.formTitle} onSubmit={handleSubmit} suppressHydrationWarning>
          <h3>{copy.formTitle}</h3>
          <DemoField
            id="demo-full-name"
            label={fields.fullName}
            placeholder={fields.fullNamePlaceholder}
            autoComplete="name"
            required
          />
          <DemoField
            id="demo-organization"
            label={fields.organization}
            placeholder={fields.organizationPlaceholder}
            autoComplete="organization"
            required
          />
          <DemoField
            id="demo-email"
            label={fields.email}
            placeholder={fields.emailPlaceholder}
            type="email"
            autoComplete="email"
            required
          />
          <DemoField
            id="demo-phone"
            label={`${fields.phone} (${fields.optional})`}
            placeholder={fields.phonePlaceholder}
            type="tel"
            autoComplete="tel"
          />
          <label className={styles.demoField} htmlFor="demo-message" suppressHydrationWarning>
            <span>{fields.message} ({fields.optional})</span>
            <textarea id="demo-message" name="message" placeholder={fields.messagePlaceholder} rows={4} />
          </label>
          <button type="submit" className={styles.demoSubmit} disabled={status === "submitting"}>
            {status === "submitting" ? copy.submitting : copy.submit}<BriefcaseBusiness aria-hidden="true" />
          </button>
          {status !== "idle" && status !== "submitting" && (
            <p className={status === "success" ? styles.demoSuccess : styles.demoError} role="status">
              {status === "success" ? copy.success : copy.error}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

type DemoFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  type?: "email" | "tel" | "text";
  autoComplete: string;
  required?: boolean;
};

function DemoField({
  id,
  label,
  placeholder,
  type = "text",
  autoComplete,
  required = false,
}: DemoFieldProps) {
  return (
    <label className={styles.demoField} htmlFor={id} suppressHydrationWarning>
      <span>{label}{required && <span aria-hidden="true"> *</span>}</span>
      <input
        id={id}
        name={id.replace("demo-", "")}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        data-lpignore="true"
        data-1p-ignore
        data-bwignore
      />
    </label>
  );
}

const featureIcons = ["🔎", "🍁", "✨", "🗣️", "🔒", "⏱️"] as const;

function FeatureSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].features;

  return (
    <section className={styles.featuresSection} id="features">
      <div className={styles.featuresInner}>
        <p className={styles.featuresEyebrow}>{copy.eyebrow}</p>
        <h2>{copy.title}</h2>
        <div className={styles.featureGrid}>
          {copy.items.map((feature, index) => {
            const icon = featureIcons[index];

            return (
              <article className={styles.featureCard} key={feature.title}>
                <div className={styles.featureMeta}>
                  <span className={styles.featureIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.featureNumber}>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuilderMockup({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].mockup;
  type PreviewSection = "profile" | "achievements" | "experience" | "education" | "additional";
  const [activeSection, setActiveSection] = useState<PreviewSection>("profile");
  const navigation = [
    { id: "profile", label: copy.profile, icon: "👤" },
    { id: "achievements", label: copy.achievements, icon: "✨" },
    { id: "experience", label: copy.experience, icon: "💼" },
    { id: "education", label: copy.education, icon: "🎓" },
    { id: "additional", label: copy.additional, icon: "🗣️" },
  ] satisfies ReadonlyArray<{ id: PreviewSection; label: string; icon: string }>;

  const previewSections: Record<PreviewSection, ReactNode> = {
    profile: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.profile}</h2>
        <p>{copy.profileText}</p>
      </section>
    ),
    achievements: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.achievements}</h2>
        <ul>{copy.achievementItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
    experience: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.experience}</h2>
        {copy.experienceItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.company}>
            <h3>{item.company} — {item.role}</h3>
            <p className={styles.resumePreviewMeta}>{item.location} | {item.dates}</p>
            <ul>{item.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
          </article>
        ))}
      </section>
    ),
    education: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.education}</h2>
        {copy.educationItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.credential}>
            <h3>{item.credential}</h3>
            <p>{item.institution} | {item.dates}</p>
          </article>
        ))}
      </section>
    ),
    additional: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.additional}</h2>
        <ul>{copy.additionalItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
  };

  return (
    <div className={styles.mockup} aria-label="CVBuilder application preview">
      <div className={styles.browserBar}>
        <span /><span /><span />
        <code>cvbuilder.ca / builder</code>
      </div>
      <div className={styles.builder}>
        <aside className={styles.sidebar}>
          <p>{copy.sections}</p>
          <ul role="tablist" aria-label={copy.sections}>
            {navigation.map(({ id, label, icon }) => (
              <li key={id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSection === id}
                  aria-controls="marketing-resume-preview"
                  className={activeSection === id ? styles.activeNavItem : undefined}
                  onClick={() => setActiveSection(id)}
                >
                  <span className={styles.previewIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.sidebarLabel}>{label}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.completion}><span />{copy.complete}</div>
        </aside>
        <div
          id="marketing-resume-preview"
          className={styles.workspace}
          role="tabpanel"
          aria-live="polite"
        >
          {previewSections[activeSection]}
        </div>
      </div>
    </div>
  );
}
